import { AxiosError } from 'axios';
import axiosInstance from '../utils/axios';

/**
 * Paid post-mock review (monetization feature #01).
 *
 * Unlike the other session calls, this one must NOT collapse failures to null: a
 * `402` means "not purchased" and drives the paywall, which is a different screen
 * from "something broke". The caller gets a discriminated result so it can tell
 * locked from broken.
 */

export interface QuestionReview {
  questionId: string;
  questionIndex: number | null;
  question?: string;
  options?: string[];
  correctAnswerIndex?: number | null;
  correctAnswer?: string;
  studentAnswerIndex?: number | null;
  studentAnswerText?: string | null;
  /** null when the question was never attempted — distinct from an incorrect answer. */
  correct: boolean | null;
  attempted: boolean;
  explanation?: string;
  hints?: string[];
  cognitiveSkill?: string;
  timeTakenSeconds?: number | null;
  markedForReview: boolean;
}

export interface SkillBreakdown {
  cognitiveSkill: string;
  attempted: number;
  correct: number;
  accuracyPct: number;
}

export interface SessionReview {
  sessionId: string;
  packageId?: string;
  score: number | null;
  totalScore: number | null;
  questions: QuestionReview[];
  skillBreakdown: SkillBreakdown[];
}

export type SessionReviewResult =
  | { state: 'ok'; review: SessionReview }
  | { state: 'locked' }
  | { state: 'unauthorized' }
  | { state: 'error'; message: string };

export const fetchSessionReview = async (sessionId: string): Promise<SessionReviewResult> => {
  try {
    const response = await axiosInstance.get(`/sessions/${sessionId}/review`);
    if (response.data?.status === 'success' && response.data?.data) {
      return { state: 'ok', review: response.data.data as SessionReview };
    }
    return { state: 'error', message: response.data?.message ?? 'Unexpected response' };
  } catch (err) {
    const status = (err as AxiosError)?.response?.status;
    // 402 is the entitlement gate in learning-service, passed through by the BFF.
    if (status === 402) return { state: 'locked' };
    if (status === 401 || status === 403) return { state: 'unauthorized' };
    if (status === 404) return { state: 'error', message: 'Session not found' };
    return { state: 'error', message: 'Could not load the review. Please try again.' };
  }
};
